// Every user-visible string in the portal, in ONE file, keyed by domain then
// name (Copy.Identity.PasswordFailMessage). Constants (codes, keys, regexes,
// enum values) live in common/constants/; only text a person reads belongs
// here, so localisation later means translating this file alone. Labels
// embedded in option lists (key/text/value collections) stay with their
// structures until a full localisation pass.
import { Auth } from "./constants/identity";

export const Copy = {
  Common: {
    InsufficientPermissions: {
      Title: "Insufficient Access",
      Message: "You do not have access to this feature. Contact your system administrator to adjust your permissions.",
    },
    ErrorMessage:
      "Oops, we didn't expect this, please let us know via info@pellerex.com if the problem persisted.",
    AlternativeErrorMessage:
      "Oops, we didn't expect this, please let us know via info@pellerex.com if the problem persisted.",
    ForbiddenErrorMessage:
      "It appears you don't have enough permission to access this function. Talk to your tenant administrator.",
    // common/http/apiCall: a server error's message, followed by the id its request is found by.
    ErrorMessageWithReference: (message, correlationId) => `${message} Reference: ${correlationId}`,
    TwoFactorAuthValidationFailedMessage:
      "This code seems to be invalid. Please check your code and try again.",
    WillBeInTouch: "Got it, we will be in touch soon.",
    NeedContributorToScan: "You need Contributor access to run a scan.",
    TryAgain: "Try again",
    Cancel: "Cancel",
    // What a view of several apps covers, by its level (Dashboard.Analytics.Levels), as a
    // sentence names it: "every app of this organisation". The tabs shown at a level word their
    // descriptions with it.
    Places: {
      AppSpace: "AppSpace",
      Organisation: "organisation",
      Tenant: "tenant",
    },
    // common/dateTimeUtils: how a duration ("88 ms") and a stretch of time ("23 minutes") are written.
    Durations: {
      Milliseconds: (amount) => `${amount} ms`,
      Seconds: (amount) => `${amount} s`,
      Minutes: (amount) => `${amount} min`,
      Hours: (amount) => `${amount} h`,
    },
    TimeSpans: {
      Second: (amount) => (amount === 1 ? "1 second" : `${amount} seconds`),
      Minute: (amount) => (amount === 1 ? "1 minute" : `${amount} minutes`),
      Hour: (amount) => (amount === 1 ? "1 hour" : `${amount} hours`),
      Day: (amount) => (amount === 1 ? "1 day" : `${amount} days`),
    },
    // common/dateTimeUtils formatLocalDayRange: the first and last day of a stretch of time.
    DayRange: (firstDay, lastDay) => `${firstDay} to ${lastDay}`,
    // components/Expandable: a pane that can take the whole window.
    Expandable: {
      Expand: "Expand to the whole window",
      Collapse: "Back to its place in the page",
    },
    // components/Search/FilterSearch: a search box that takes typed filters, with the filters it
    // has applied listed under it. A screen adds its own placeholder and its list of filters.
    FilterSearch: {
      FocusKey: "/",
      Suggestions: "Search suggestions",
      Fields: "Filter by field",
      Values: (key) => `Choose a value for ${key}`,
      NoValues: "Nothing to choose from here. Type the value and press Enter.",
      Recent: "Recent searches",
      NoRecent: "Searches you run appear here.",
      StoredHere: "Recent searches are kept in this browser.",
      Tips: {
        ExcludeKey: "-",
        Exclude: (example) => `in front of a filter excludes it, as in -${example}`,
        UpKey: "Up",
        DownKey: "Down",
        Move: "move",
        EnterKey: "Enter",
        Apply: "apply",
        EscapeKey: "Esc",
        Close: "close",
      },
      Filters: {
        Label: "Filters",
        ClearAll: "Clear all",
        Remove: (filter) => `Remove filter ${filter}`,
      },
    },
    // components/LogTerminal: a live log as a machine's terminal shows it, "Logs(source)[tail]".
    LogTerminal: {
      Title: "Logs",
      Mode: "[tail]",
      Autoscroll: "Autoscroll",
      Wrap: "Wrap",
      On: "On",
      Off: "Off",
      Live: "Live",
      Paused: (waitingLines) =>
        waitingLines === 1 ? "Paused, 1 new line waiting" : `Paused, ${waitingLines} new lines waiting`,
      StatusConnecting: "Connecting",
      StatusEnded: "Ended",
      Pause: "Pause",
      Resume: "Resume",
      CopyAll: "Copy all",
      Reconnect: "Reconnect",
      Connecting: "Connecting to the log stream.",
      Empty: "No log lines yet.",
      LinesSkipped: (count) => (count === 1 ? "1 line skipped" : `${count} lines skipped`),
    },
    // components/Filters/FilterAccordion: the slim bar a pane's filters fold into.
    FilterAccordion: {
      Title: "Filters",
    },
    // components/Environments/EnvironmentSelect: the picker's choice that filters by no environment.
    EnvironmentSelect: {
      AllEnvironments: "All environments",
    },
    // components/DateTime/TimeRangePicker: a range of time, chosen from a screen's own presets or
    // as two instants.
    TimeRangePicker: {
      To: "to",
      From: "From, local time",
      Until: "To, local time",
      Earlier: "Move the time range earlier",
      Later: "Move the time range later",
    },
    // components/DateTime/PeriodBar: the bar above a tab's charts that picks the period they cover.
    PeriodBar: {
      Label: "Period",
      // By the period, one of Dashboard.Analytics.TimeRanges.
      Periods: {
        "last-1-hour": "1 hour",
        "last-24-hours": "24 hours",
        "last-7-days": "7 days",
        "last-30-days": "30 days",
        "last-90-days": "90 days",
        "last-180-days": "180 days",
        "last-365-days": "365 days",
      },
    },
    // components/Tables/NumberedPagination: the row of page numbers under a list read a page at a time.
    NumberedPagination: {
      Label: "Pages",
      Previous: "Previous page",
      Next: "Next page",
      Page: (page) => `Page ${page}`,
      // Where a run of pages is left out of the row.
      Gap: "…",
    },
    // components/Pipeline Steps and StepLog: the steps of a pipeline run (a build, or a deployment
    // to one environment), each opening to its log.
    PipelineSteps: {
      Loading: "Loading steps…",
      NoSteps: "No steps to show.",
      ExpandAll: "Expand",
      CollapseAll: "Collapse",
      NoLogOutput: "This step produced no log output: it was skipped, or finished without writing anything.",
      LogAfterRun: "The log shows once the run has finished.",
    },
    // components/Indicators/PlatformSeverityBadge: how serious the platform rates a problem, by
    // Engineering.PlatformHealth.Severities.
    PlatformSeverityBadge: {
      Critical: "Critical",
      High: "High",
      Medium: "Medium",
      Low: "Low",
    },
  },
  Identity: {
    SignIn: "Sign In",
    SignUp: "Sign Up",
    PasswordFailMessage:
      "Passwords must have at least 8 characters, 1 lowercase, 1 upper case, 1 number, and 1 special character.",
    // The sign-in page's form, with its Sign Up and Sign In tabs.
    AuthForm: {
      // Under the email on the Sign Up tab. Identity makes no account for an email with a plus sign
      // in it; the Sign In tab takes any email.
      SignUpEmailInvalid: "A valid email address without a plus sign (+) is required to sign up.",
    },
    ExternalLoginFailedGeneralMessage:
      "External login failed. Please try again or contact us.",
    LocalLoginFailedGeneralMessage:
      "Login failed. Please try again or contact us.",
    UserDisabledMessage:
      "Your account has been blocked, please contact us for further assistance.",
    DuplicateUserMessage:
      "This email is already registered. Sign in with the method you used to create your account, or use a different email to register.",
    PasswordResetLinkMessageSent: `You would receive an email shortly if a password-protected account existed in our system. Alternatively you may have created your account with us through Google/Microsoft social logins.`,
    TokenDeleteSuccess: "Token deleted successfully.",
    TokenDeleteError: "Failed to delete token. Please try again.",
    TokenRevokeSuccess: "Token revoked successfully.",
    TokenRevokeError: "Failed to revoke token. Please try again.",
    // The Identity tab's form for a new token.
    NewToken: {
      ExpiryRequired: "Expiry is required.",
      // Under an expiry day before tomorrow.
      ExpiryTooEarly: "The expiry can be tomorrow at the earliest.",
      // Under an expiry day later than the last one the form offers (Auth.ProductAccessTokens.LongestLifeInDays).
      ExpiryTooLate: "A token can last at most a year.",
    },
    // The Identity tab's one line naming the Pellerex platform services that may call the app
    // through managed identities, as IdentityApi lists them. Read-only: nothing is made or changed
    // from the portal.
    ManagedIdentities: {
      // The Pellerex service behind each identity, keyed by the purpose IdentityApi sends.
      Services: {
        [Auth.ManagedIdentityPurposes.HealthChecks]: "monitoring",
        [Auth.ManagedIdentityPurposes.TestRuns]: "test runner",
      },
      // The line, given the services that are on, already joined in words ("monitoring and test runner").
      MayCall: (services) => `Pellerex's ${services} may call this app through managed identities.`,
    },
  },
  Marketplace: {
    // The AppSpace overview (the dashboard's Home): the Quick Actions section, then the Activity Feed
    // heading and its one strip of tabs. A tab's title is also its address (?tab=jobs), so renaming
    // one changes its link.
    AppSpaceOverview: {
      ActivityFeed: {
        Title: "Activity Feed",
        Subtitle: "Recent activity across your apps",
      },
      LiveTraffic: {
        Title: "Live Traffic",
      },
      Apps: {
        Title: "Apps",
      },
      Alerts: {
        Title: "Alerts",
        ComingSoonTitle: "Coming soon",
        ComingSoonDescription: "Alerts will appear here.",
      },
      Jobs: {
        Title: "Jobs",
      },
      Deployments: {
        Title: "Deployments",
        // Above the list when the latest of its timed refreshes failed and the list read before stays.
        RefreshFailed: "The latest refresh failed. Showing the previous list.",
        // The band over an app's deployments, for an app the app space no longer lists.
        UnknownApp: "Unknown app",
        // In place of the list when no app has a deployment to the environment chosen, given its name.
        NoneToEnvironment: (environmentName) => `No deployments to ${environmentName} yet.`,
      },
      QuickActions: {
        Title: "Quick Actions",
        Subtitle: "Jump into your Day 0, Day 1, and Day 2 workflows",
      },
      TrafficAnalytics: {
        Title: "Traffic Analytics",
      },
      LicenseConsumption: {
        Title: "License Consumption",
      },
      PlatformInsights: {
        Title: "Platform Insights",
        Subtitle: "Health scores and compliance posture",
      },
    },
    // The Summary Usage Stats chart of an API's assets and of a service's page: the calls ProxyApi
    // counted for the app on each day, by how they ended.
    UsageStats: {
      Title: "Summary Usage Stats",
      Subtitle: "the number of successful & failed calls along with the limits reached.",
      DateAxis: "Date",
      CallsAxis: "API Calls",
      Succeeded: "Successful Calls",
      Failed: "Failed Calls",
      LimitReached: "Limit Reached",
      // Under a kind of call's name: the day under the pointer, and how many such calls it had.
      TotalCount: (day, count) => `Total Count for ${day}: ${count}`,
    },
    AccessMessages: {
      NoPermissionToEditRoles: "You do not have permission to edit roles.",
      NoPermissionToRemoveUsers: "You do not have permission to remove users.",
      NoPermissionToRemoveTeams: "You do not have permission to remove teams.",
      NoPermissionToAddUsers: "You do not have permission to add users.",
      NoPermissionToAddTeams: "You do not have permission to add teams.",
      NoPermissionToInviteMembers: "You do not have permission to invite new members.",
      NoPermissionToDeleteInvitations: "You do not have permission to delete invitations.",
      NoPermissionToDeleteApp: "Only an owner can remove this application.",
      CannotModifyOwnRole: "You cannot modify your own role.",
      CannotRemoveOwnAccess: "You cannot remove your own access.",
      CannotRemoveInherited: "This permission is inherited and cannot be deleted at this level.",
      CannotRemoveOwnOrInherited: "You can not remove your permissions or inherited ones.",
      // Role-tier tooltips — used wherever a UI element is disabled because the
      // user lacks the required role. Action context comes from the button/label;
      // the tooltip names the missing tier.
      RequiresReaderAccess: "You need at least Reader permission to perform this action.",
      RequiresContributorAccess: "You need at least Contributor permission to perform this action.",
      RequiresOwnerAccess: "You need at least Owner permission to perform this action.",
    },
    // Under the name of an AppSpace, in the forms that make one and edit one: the Add and Edit panels
    // of an organisation's AppSpaces, and the Basics tab of an AppSpace.
    AppSpaceNames: {
      // A new AppSpace, in the words of the rule Marketplace makes it with.
      NewNameInvalid:
        "A valid AppSpace name is required: lowercase letters, numbers and hyphens, starting with a letter and ending with a letter or number, up to 63 characters (for example payments-2026).",
      // An AppSpace being edited. Every name the rule for a new AppSpace makes passes this one.
      EditedNameInvalid: (maximumLength) =>
        `A valid AppSpace name (letters, numbers and hyphens, up to ${maximumLength} characters) is required.`,
    },
    // The first two tabs of the tenant, organisation and AppSpace management pages: Basics, the
    // name and details of the tenant, organisation or AppSpace, and Settings, one card for each
    // setting of that level.
    SpaceManagementTabs: {
      Basics: "Basics",
      Settings: "Settings",
    },
    // The Settings tab of the tenant, an organisation and an AppSpace
    // (screens/Marketplace/Dashboard/Shared/SpaceSettings). place, in each phrase: what the level is
    // called in a sentence (Copy.Common.Places).
    SpaceSettings: {
      SectionTitle: "Settings",
      SectionDescription: (place) => `Settings that cover everything in this ${place}, one card each.`,
      // Whether Pellerex keeps a transcript of what each AI call at the level sends and gets back,
      // which the Proxy stores.
      KeepTranscriptsOfAiCallsCard: {
        Title: "Keep transcripts of AI calls",
        Description: (place) =>
          `Pellerex keeps what each AI call it lets through in this ${place} sends and gets back for 90 days, with card numbers and national ids masked, and turning this off stops new transcripts and hides the ones kept.`,
        // Under the switch, for anyone but the owner of the level or of a level above it.
        OnlyOwnerCanChange: (place) => `Only the owner of this ${place} can change this.`,
        // Under the switch when the owner of a level above turned transcripts off. place: that level.
        TurnedOffAbove: (place) => `Turned off for the whole ${place}`,
        LoadFailed: "Whether transcripts of AI calls are kept could not be read.",
        TurnedOnTitle: "Transcripts turned on",
        TurnedOnMessage: (place) => `Pellerex keeps a transcript of each new AI call in this ${place}.`,
        TurnedOffTitle: "Transcripts turned off",
        TurnedOffMessage: (place) => `Pellerex keeps no transcript of new AI calls in this ${place}, and hides the ones kept.`,
        NotChangedTitle: "Setting not changed",
      },
    },
    Teams: {
      Title: "Teams",
      Subtitle: "A team is given a role on an app, an AppSpace, an organisation or the tenant, and every member of the team gets that role.",
      AddTeam: "Add team",
      EditTeam: "Edit team",
      DeleteTeam: "Delete team",
      Members: "Members",
      Edit: "Edit",
      Delete: "Delete",
      Remove: "Remove",
      TryAgain: "Try again",
      NoTeams: "This organisation has no teams yet.",
      TeamsNotRead: "The teams could not be read.",
      NoPermissionToManageTeams: "You do not have permission to manage teams.",
      NoPermissionToDeleteTeams: "You do not have permission to delete teams.",
      MemberCount: (count) => (count === 0 ? "No members" : count === 1 ? "1 member" : `${count} members`),
      AddTeamIntro: (organisationName) => `Create a team in ${organisationName}. Its members are added once it is made.`,
      EditTeamIntro: "Change the name and the description of this team.",
      NameLabel: "Name",
      NamePlaceholder: "Name of the team",
      NameInvalid: (maximumLength) => `A team name of up to ${maximumLength} characters is required.`,
      DescriptionLabel: "Description",
      DescriptionPlaceholder: "What is this team for? (optional)",
      DescriptionInvalid: (maximumLength) => `The description can be up to ${maximumLength} characters.`,
      Save: "Save",
      SaveChanges: "Save changes",
      TeamCreated: "The team was created.",
      TeamUpdated: "The team was updated.",
      TeamDeleted: "The team was deleted.",
      DeleteWarning: (teamName) => `Delete ${teamName}? Every role the team holds is removed with it, so its members lose the access the team gave them. This cannot be undone.`,
      TeamMembers: "Team members",
      MembersIntro: (teamName) => `The people in ${teamName}. Each of them gets every role the team holds.`,
      NoMembers: "This team has no members yet.",
      SelectedPerson: "Selected person",
      AddToTeam: "Add to team",
      AlreadyAMember: "This person is already in the team.",
      MemberAdded: "The person was added to the team.",
      MemberRemoved: "The person was removed from the team.",
      UnknownTeam: "Unknown team",
      TeamAccess: "Team access",
      TeamRoleGiven: "The team was given the role.",
      TeamRoleTaken: "The role was taken away from the team.",
      // Two organisations may each have a team of the same name, so a list that holds the teams
      // of the whole tenant names the organisation beside the team.
      TeamOfOrganisation: (teamName, organisationName) => `${teamName} (${organisationName})`,
    },
    PermissionTreeView: {
      SearchPlaceholder: "Search by name, role, or ID…",
      EmptyBadgeText: "No role allocated",
      RoleThroughTeam: (roleName, teamName) => `${roleName}, through ${teamName}`,
    },
    ManagedApiFAQs: [
      {
        id: 1,
        question: "How long does it take for a new API to be up and running?",
        answer:
          "Within seconds! Anytime you provision a new API, a scaffolded repository, CI/CD pipelines and Azure KeyVaults for QA, Staging and Prod environments are also creatd, which means your API with a Hello World endpoint is operation on the Cloud within seconds.",
      },
      {
        id: 1,
        question: "What languages and stacks do you support?",
        answer:
          "All. Pellerex is a language agnostic platform. Right now we support .NET while Python and Node are coming soon. If you need any specific one, reach out and it will be supported within two weeks.",
      },
      {
        id: 2,
        question: "How many environments does each API have?",
        answer: "Three. QA, Staging, and Production.",
      },
      {
        id: 3,
        question:
          "Do deployments to higher environments need manual approval?",
        answer:
          "Yes, deployment to Staging and Production need manual approval.",
      },
      {
        id: 4,
        question:
          "Are my API endpoints protected? If yes, what is the authentication mechanism?",
        answer:
          "All APIs run on Pellerex are protected using OAuth and Json Web Token (JWT). When you want to consume your API, call our Identity endpoint API, retrieve a bearer token, and supply that in your API request.",
      },
      {
        id: 5,
        question: "What infrastructure do you use to run the APIs?",
        answer:
          "Kubernetes is the orchestrator use to run containerised API on the Cloud.",
      },
      {
        id: 6,
        question: "Do you provide secret management capability?",
        answer:
          "Yes, your API comes with nativ secret management capability provided by fully secure Cloud based KeyVaults. You can manage your secrets in your dashboard.",
      },
      {
        id: 7,
        question: "Do APIs have access to any databases?",
        answer:
          "Yes. As secret management is provided out of the box, you can connect with any Cloud based database of your choice. ",
      },
      {
        id: 8,
        question:
          "How do you manage configurations across different environments?",
        answer:
          "As code. Configurations in various environments are managed as code within your repository. Each environment has a separate configuration file.",
      },
      {
        id: 9,
        question:
          "What if I needed any of the above behaviour changed for my company?",
        answer:
          "Enterprise tier allows you to have our ecosystem customised. For example if you needed four environments instead of three, that's fully achievable when we run an instance of Pellerex, just for you!",
      },
    ],
  },
  Onboarding: {
    // Keyed by the error codes the naming confirmation endpoints return;
    // OwnerRequestFailed is portal-local (the request call itself failed).
    NamingErrorMessages: {
      TenantNameTaken: "A tenant with this name already exists. Choose a different name.",
      OrganisationNameTaken: "An organisation with this name already exists. Choose a different name.",
      AppSpaceNameTaken: "An AppSpace with this name already exists in your organisation. Choose a different name.",
      NotTenantOwner: "Only a tenant owner can confirm workspace naming.",
      OwnerRequestFailed: "Sending the request failed. Please try again.",
    },
  },
  Subscription: {
    SubscriptionSuccessful:
      "Success, head to your dashboard now to access your plan's benefits.",
    // The License Consumption panel: an amount of a plan limit written in its unit ("100 MB"), and
    // the pill that says how long a call limit's window is.
    LicenseConsumption: {
      Megabytes: (amount) => `${amount} MB`,
      // Keyed by the call limit's interval (Product.Pricing.Intervals): the Proxy counts the calls
      // in a window of that length, measured from the plan's start.
      CallLimitIntervals: {
        Monthly: "Per month",
        Daily: "Per day",
        Hourly: "Per hour",
      },
    },
    // Licensing/Account/Common/LicenseCheck: what shows in place of a feature the plan decides.
    LicenseCheck: {
      // Over the reason and a Try again button, when the plan could not be checked.
      PlanNotChecked: "Your plan could not be checked",
      // The reason when nothing answered the check, so there are no words from Marketplace to show.
      NotReached: "Pellerex could not be reached. Check your internet connection, or try again in a few minutes.",
    },
  },
  Engineering: {
    // The Infra tab: one health-first card per environment, with a container drill-in. Shown for
    // one app, or for every app of an AppSpace, organisation or tenant together.
    Infrastructure: {
      SectionTitle: "Pods and Containers",
      SectionDescription:
        "Health, resource use and uptime of your service in each environment. This view refreshes every 30 seconds, and its CPU, memory and pods charts every 5 minutes.",
      // place: what a view of several apps covers (Copy.Common.Places).
      SeveralAppsDescription: (place) =>
        `Health, resource use and uptime of every app of this ${place} in each environment, together. This view refreshes every 30 seconds, and its CPU, memory and pods charts every 5 minutes.`,
      EnvironmentCardSubtitle: "Pods, containers and uptime",
      Health: {
        Healthy: "Healthy",
        Degraded: "Degraded",
        Down: "Down",
        NotDeployed: "Not deployed",
      },
      Stats: {
        RunningPods: "Running pods",
        PendingPods: "Pending pods",
        ActiveContainers: "Active containers",
      },
      UpdatedAgo: (relativeTime) => `Updated ${relativeTime}`,
      RefreshFailed: "The latest refresh failed. Showing the previous reading.",
      LoadFailed: "This environment's pods and containers could not be read.",
      NoPods: "No pods are running in this environment yet.",
      SeveralAppsNoPods: (place, environmentName) => `No app of this ${place} has pods in ${environmentName} yet.`,
      PodNoContainers: "This pod reports no containers yet.",
      // The full-width card under the environment cards: the chosen environment's pods and containers.
      Workload: {
        Title: (environmentName) => `${environmentName} pods and containers`,
        Subtitle: 'Each container\'s status and use against its limits. A container\'s "..." menu opens its live logs or metrics.',
        Show: "Show pods and containers",
        Shown: "Pods and containers shown below",
        // In a view of several apps: the line under an app's header when it has no pods in the environment.
        AppNoPods: (environmentName) => `This app has no pods in ${environmentName}.`,
        // In a view of several apps: the box above the table that narrows it to the apps, pods and
        // containers whose names hold what is typed, and the line in the table's place when none does.
        Search: {
          Label: "Search apps, pods and containers",
          Placeholder: "Find an app, pod or container",
          // text: the search as typed, without the spaces around it.
          NoMatch: (environmentName, text) => `No app, pod or container in ${environmentName} matches "${text}".`,
        },
        Columns: {
          Container: "Container",
          Status: "Status",
          Restarts: "Restarts",
          Age: "Age",
          Actions: "Actions",
        },
      },
      Containers: {
        Ready: "Ready",
        NotReady: "Not ready",
        Restarts: (restartCount) => (restartCount === 1 ? "1 restart" : `${restartCount} restarts`),
        CrashLooping: "Crash looping",
        Age: (age) => `Age ${age}`,
        ImageTag: "Image tag",
        Actions: (containerName) => `Actions for ${containerName}`,
        ViewLogs: "View logs",
        ViewMetrics: "View metrics",
        RestartPod: "Restart pod",
        Terminating: "Terminating",
      },
      // The confirmation a pod restart asks for, and how its outcome reads.
      RestartPod: {
        Title: "Restart this pod?",
        Description: (podName, environmentName) =>
          `Pod ${podName} in ${environmentName} will be stopped and a new pod started in its place. Requests it is handling are interrupted, and if it is the only pod the service is unavailable until the new one is ready.`,
        Confirm: "Restart pod",
        RequestedTitle: "Pod restart requested",
        Requested: (podName) => `Pod ${podName} is stopping. A new pod will replace it shortly.`,
        FailedTitle: "Pod not restarted",
        NoAccess:
          "You do not have permission to restart pods in this environment. Contact your system administrator to adjust your permissions.",
        NotFound: "This pod no longer exists.",
        NotRestartable: "This pod is not managed by a deployment, so nothing would replace it after a restart.",
        Failed: "The pod could not be restarted. Please try again shortly.",
      },
      Resources: {
        Memory: "RAM",
        Cpu: "CPU",
        Request: "Request",
        Limit: "Limit",
        NotSet: "Not set",
        ShareOfLimit: (percent) => `${percent}% of limit`,
        ShareOfRequest: (percent) => `${percent}% of request`,
        NoReading: "No reading yet",
        UsedOfLimit: (used, limit) => `${used} of ${limit}`,
        UsedOfRequest: (used, request) => `${used} of ${request} requested`,
      },
      Units: {
        Mebibytes: "MiB",
        Gibibytes: "GiB",
        MilliCores: "m",
        Cores: "cores",
      },
      Uptime: {
        Title: "Uptime",
        Window: (windowDays) => `Last ${windowDays} days`,
        Checks: (checkCount) => `${checkCount} checks`,
        ChecksSince: (checkCount, since) => `${checkCount} checks since ${since}`,
        NoHistory: "No uptime history yet",
        ReadFailed: "Uptime could not be read.",
      },
      // The CPU, memory and pods charts under the pods and containers card: each app's use and pods
      // ready over the period chosen in the bar above them, from the readings AssetHealth takes every
      // 5 minutes.
      ResourceUsageOverTime: {
        CpuUse: { Title: "CPU use" },
        MemoryUse: { Title: "Memory use" },
        // A point of the pods chart is the fewest pods ready in its bucket rather than an average, so
        // the chart has words of its own, in the CPU and memory charts' form.
        PodsReady: {
          Title: "Pods ready",
          // bucket: how long each point covers (BucketWidths), once AssetHealth has answered.
          Subtitle: (bucket) =>
            bucket ? `The fewest of this app's pods ready per ${bucket}` : "The fewest of this app's pods ready",
          // place: what a view of several apps covers (Copy.Common.Places).
          SeveralAppsSubtitle: (place, bucket) =>
            bucket
              ? `One line per app of this ${place}, the fewest of its pods ready per ${bucket}`
              : `One line per app of this ${place}, the fewest of its pods ready`,
          // Under the pointer: the fewest pods ready in the bucket, of the most pods wanted in it.
          ReadyOfWanted: (ready, wanted) => `${ready} of ${wanted} ready`,
          NoReadings: "Pods are counted every 5 minutes, and no count is there yet for this period.",
          LoadFailed: "Pods ready could not be read.",
        },
        // bucket: how long each point of a line averages (BucketWidths), once AssetHealth has answered.
        Subtitle: (bucket) =>
          bucket ? `All of this app's containers together, averaged per ${bucket}` : "All of this app's containers together",
        // place: what a view of several apps covers (Copy.Common.Places).
        SeveralAppsSubtitle: (place, bucket) =>
          bucket
            ? `One line per app of this ${place}, all its containers together, averaged per ${bucket}`
            : `One line per app of this ${place}, all its containers together`,
        // How long each point covers, by the minutes AssetHealth names it with (bucketMinutes).
        BucketWidths: {
          5: "5 minutes",
          60: "hour",
          240: "4 hours",
          1440: "day",
        },
        NoReadings: "Readings are taken every 5 minutes, and none are there yet for this period.",
        LoadFailed: "CPU and memory use could not be read.",
      },
      Events: {
        Title: "Recent events",
        Empty: "No recent events.",
        Count: (count) => (count === 1 ? "once" : `${count} times`),
        Columns: {
          LastSeen: "Last seen",
          // In a view of several apps: the app each event is about.
          App: "App",
          Type: "Type",
          Reason: "Reason",
          Message: "Message",
          Count: "Count",
        },
      },
      // The panel under the environment cards, for the container a "..." command chose.
      ContainerPanel: {
        Subtitle: (podName, environmentName) => `Pod ${podName} in ${environmentName}`,
        Tabs: {
          Metrics: "Metrics",
          LiveLogs: "Live logs",
        },
        ContainerGone: "This container is not in the latest reading. Its pod may have been replaced.",
      },
      Metrics: {
        State: "State",
        StateUnknown: "Unknown",
        Ready: "Ready",
        Restarts: "Restarts",
        CrashLooping: "Crash looping",
        Started: "Started",
        Age: (age) => `${age} ago`,
        ImageTag: "Image tag",
        Yes: "Yes",
        No: "No",
      },
      LiveLogs: {
        OwnershipNotice:
          "Pellerex infrastructure details are redacted. Your application owns what it writes to its logs.",
        Ended: "Stream ended.",
        EndReasons: {
          MaxDuration: "The stream reached its maximum duration.",
          Idle: "The stream closed after a period with no new lines.",
          StreamClosed: "The container closed its log stream.",
          Error: "The stream stopped because of a server error.",
        },
        NoAccess:
          "You do not have access to this environment's container logs. Contact your system administrator to adjust your permissions.",
        OpenFailed: "The log stream could not be opened.",
        SessionExpired: "Your sign-in needs renewing. The stream reconnects once the page has renewed it.",
        NotFound: "This pod or container no longer exists.",
        TooManyStreams: "Too many log streams are open right now. Close another stream, then reconnect.",
      },
    },
    // The Uptime tab: whether the apps answer their health address, from AssetHealth's check of each
    // app once a minute, in one environment at a time. Shown for one app, or for every app of an
    // AppSpace, organisation or tenant together.
    Uptime: {
      SectionTitle: "Uptime",
      SectionDescription:
        "Whether your app answers its health address in each environment, from a check once a minute. Uptime is the share of checks it answered; downtime starts after two failed checks in a row that were your app's own. This view refreshes every 30 seconds.",
      // place: what a view of several apps covers (Copy.Common.Places).
      SeveralAppsDescription: (place) =>
        `Whether the apps of this ${place} answer their health address in each environment, from a check of each app once a minute. Uptime is the share of all their checks answered, each app's counting on its own; downtime is any app down, from two failed checks in a row of its own. This view refreshes every 30 seconds.`,
      EnvironmentLabel: "Environment",
      NoChecks: (environmentName) =>
        `This app has no checks in ${environmentName} yet. It is checked once a minute from when it is deployed there.`,
      SeveralAppsNoChecks: (place, environmentName) => `No app of this ${place} has checks in ${environmentName} yet.`,
      LoadFailed: "Uptime could not be read.",
      RefreshFailed: "The latest refresh failed. Showing the previous reading.",
      // How long each point of the uptime over time and response time charts covers, by the minutes
      // AssetHealth names it with (bucketMinutes).
      BucketWidths: {
        1: "minute",
        5: "5 minutes",
        60: "hour",
        240: "4 hours",
        1440: "day",
      },
      CurrentStatus: {
        Title: "Current status",
        // By the status AssetHealth sends (Engineering.Uptime.Statuses).
        Statuses: {
          Up: "Up",
          Down: "Down",
          Degraded: "Degraded",
        },
        // Before how long the status has held ("1d 19h 35m").
        For: {
          Up: "Currently up for",
          Down: "Currently down for",
          Degraded: "Currently degraded for",
        },
        Degraded: "Fewer pods are ready than should be.",
        // apps: the apps' names as a list ("Orders API and Billing API"); count: how many there are.
        AppsDown: (apps, count) => (count === 1 ? `${apps} is down.` : `${apps} are down.`),
        AppsDegraded: (apps, count) => (count === 1 ? `${apps} is degraded.` : `${apps} are degraded.`),
        // The names before the last of a short list, and the last.
        AndTheLast: (names, last) => `${names} and ${last}`,
        // The first names of a long list, and how many more there are.
        AndMore: (names, more) => `${names} and ${more} more`,
      },
      LastCheck: {
        Title: "Last check",
        // interval: how often each app is checked, in words ("60 seconds").
        Every: (interval) => `Checked every ${interval}`,
        // endpoint: the path the app's newest check called ("/health/ready").
        Endpoint: (endpoint) => `Calls ${endpoint}`,
      },
      // The card of the period chosen in the bar: its uptime and downtime, its checks, and a strip
      // of bars across it.
      Range: {
        // The card's title, by the period AssetHealth sends (Dashboard.Analytics.TimeRanges).
        Names: {
          "last-1-hour": "Last hour",
          "last-24-hours": "Last 24 hours",
          "last-7-days": "Last 7 days",
          "last-30-days": "Last 30 days",
          "last-90-days": "Last 90 days",
          "last-180-days": "Last 180 days",
          "last-365-days": "Last 365 days",
        },
        // downtime: how long at least one app was down, in words ("55 minutes").
        Downtime: (downtime) => `${downtime} down`,
        NoDowntime: "No downtime",
        ChecksSince: (checkCount, since) => `${checkCount} checks since ${since}`,
        // No check counted: none made in the period, or only part of a day older than the checks
        // AssetHealth keeps, which it counts by whole days.
        NoChecks: "No checks counted",
        StripLabel: "Uptime in each bar of the period",
        // period: the period's length in the bar's words ("24 hours").
        Start: (period) => `${period} ago`,
        End: "Now",
        // A bar on hover: its local start, what it was and, when it was checked, its checks.
        Bar: (start, words) => `${start}: ${words}`,
        BarStatuses: {
          Up: "up",
          Down: "down",
          Degraded: "degraded",
          NotChecked: "not checked",
        },
        BarChecks: (words, percent, checkCount) => `${words}, ${percent} of ${checkCount} checks answered`,
      },
      // The chart of the apps' uptime across the period chosen in the bar, drawn as Traffic's
      // response time trends are.
      OverTime: {
        Title: "Uptime over time",
        // bucket: how long each point covers (BucketWidths).
        Subtitle: (bucket) => `The share of this app's checks answered per ${bucket}`,
        // place: what a view of several apps covers (Copy.Common.Places).
        SeveralAppsSubtitle: (place, bucket) =>
          `One line per app of this ${place}, the share of its checks answered per ${bucket}`,
        Average: "Average",
        Lowest: "Lowest",
        NoChecks: "No app was checked in this period.",
      },
      ResponseTimes: {
        Title: "Response time",
        // bucket: how long each point averages (BucketWidths), once AssetHealth has answered.
        Subtitle: (bucket) =>
          bucket
            ? `How long this app took to answer its health address, averaged per ${bucket}`
            : "How long this app took to answer its health address",
        // place: what a view of several apps covers (Copy.Common.Places).
        SeveralAppsSubtitle: (place, bucket) =>
          bucket
            ? `One line per app of this ${place}, how long it took to answer its health address, averaged per ${bucket}`
            : `One line per app of this ${place}, how long it took to answer its health address`,
        Average: "Average",
        Fastest: "Fastest",
        Slowest: "Slowest",
        NoAnswers: "No answer to a health check in this period.",
        LoadFailed: "Response times could not be read.",
      },
    },
    // The Logs tab: one search of a product's log records in an environment. The whole search
    // lives in the address bar, so a copied link opens the same view.
    LogSearch: {
      SectionTitle: "Logs",
      SectionDescription:
        "Requests, dependencies, traces and exceptions your application reported. The address bar always holds the whole search, so a copied link opens the same view.",
      // place: what a search of several apps is a search of (Copy.Common.Places).
      SeveralAppsDescription: (place) =>
        `Requests, dependencies, traces and exceptions every app of this ${place} reported, together. The address bar always holds the whole search, so a copied link opens the same view.`,
      NoAccess:
        "You do not have access to this environment's logs. Contact your system administrator to adjust your permissions.",
      TooManyApps: {
        Title: (place) => `This ${place} has more apps than one search can read`,
        Description: "Name the apps to search in the search box, such as app:payments-api, and the search reads those.",
      },
      NotInPlan: {
        Title: "Logs are not in your plan",
        Description: "Upgrade your plan to search your application's requests, dependencies, traces and exceptions.",
      },
      Modes: {
        explore: { Label: "Explore", Question: "Look around with free search and filters." },
        release: { Label: "After a release", Question: "Did this release break anything?" },
        abnormal: { Label: "What's abnormal", Question: "Is something wrong right now?" },
        feature: { Label: "Watch a feature", Question: "Is this feature healthy?" },
        trace: { Label: "Trace a request", Question: "What happened to this one request?" },
      },
      ModeBar: {
        Release: "Release",
        ChooseDeployment: "Choose a deployment",
        Deployment: (name, when) => `${name}, deployed ${when}`,
        DeploymentTo: (releaseName, environmentName) => `${releaseName} to ${environmentName}`,
        ReleasedAt: "Released at",
        ReleasedAtHint: "When the release went out, local time",
        NoDeployments: "No deployments are recorded for this app yet. Enter when the release went out.",
        // In a search of an AppSpace a release is named with the app that was released.
        DeploymentOfApp: (appName, deployment) => `${appName}: ${deployment}`,
        NoDeploymentsOfApps: "No deployments are recorded for these apps yet. Enter when the release went out.",
        DeploymentsFailed: "The deployments could not be read. Enter when the release went out.",
        Comparing: (since, before) => `Comparing the ${since} since this release with the ${before} before it.`,
        ChooseRelease: "Choose a deployment, or enter when the release went out, to compare before and after.",
        CompareWith: "Compare with",
        Baselines: {
          previous: "Previous period",
          yesterday: "Same time yesterday",
          lastweek: "Same time last week",
        },
        BaselineNames: {
          previous: "the previous period",
          yesterday: "the same time yesterday",
          lastweek: "the same time last week",
        },
        // A window longer than a day (or a week) is compared with the same length of time further back.
        BaselineEarlier: (span) => `the same period ${span} earlier`,
        AbnormalHint: "Anything new, growing or slower than it was then is listed below.",
        Feature: "Feature",
        FeaturePlaceholder: "A route prefix, such as /v1/payments",
        FeatureHint: "Every record of the requests whose route contains this text.",
        Watch: "Watch",
        Watching: "Watching",
        StopWatching: "Stop watching",
        Request: "Request",
        RequestPlaceholder: "Request ID or correlation ID",
        RequestHint: "Paste a request ID, or the correlation ID from a customer's error message.",
        Find: "Find",
        Showing: "Showing",
        ClearRequest: "Clear",
      },
      Search: {
        Label: "Search logs",
        Placeholder: "Search messages, paths and IDs, or filter with field:value",
      },
      Suggestions: {
        // What the typed search understands, in the order the list shows it. A field that takes
        // a fixed set of values shows every one of them; the others show a few ways to write one.
        // Every example is a filter the typed search reads as written (a test holds them to that).
        FieldHints: [
          {
            key: "type",
            description: "The kind of record",
            examples: ["type:request", "type:dependency", "type:trace", "type:exception"],
          },
          { key: "outcome", description: "How the record ended", examples: ["outcome:success", "outcome:warning", "outcome:error"] },
          { key: "origin", description: "Health checks, or everything else", examples: ["origin:health", "origin:app"] },
          {
            key: "status",
            description: "A request's HTTP status: one code, several, or a whole class",
            examples: ["status:5xx", "status:4xx", "status:404", "status:500,503"],
          },
          { key: "path", description: "Text the request's route contains", examples: ["path:/v1/orders", "path:payments"] },
          { key: "duration", description: "How long it took, at least", examples: ["duration:>500ms", "duration:>1s", "duration:>2m"] },
          {
            key: "target",
            description: "What a dependency called: a host, a database, a queue",
            examples: ["target:redis-orders", 'target:"sql-orders-prod | ordersdb"'],
          },
          {
            key: "exception",
            description: "Exception type, with or without its namespace",
            examples: ["exception:SqlException", "exception:System.TimeoutException"],
          },
          {
            key: "operation",
            description: "The whole route a request hit, with its method",
            examples: ['operation:"GET /v1/orders/{id}"', 'operation:"POST /v1/orders"'],
          },
          { key: "request", description: "Every record from one request, by its ID", examples: ["request:7f3a9c1e4b2d"] },
        ],
        // The one more filter a search of an AppSpace reads. Its examples name the AppSpace's own apps.
        AppHint: { key: "app", description: "The app that wrote the record" },
      },
      Filters: {
        None: "None yet. Tick values in the list below, pick a quick filter, or type field:value in the search box.",
      },
      Time: {
        Label: "Time",
        Windows: {
          "5m": "Last 5 minutes",
          "15m": "Last 15 minutes",
          "30m": "Last 30 minutes",
          "1h": "Last hour",
          "3h": "Last 3 hours",
          "6h": "Last 6 hours",
          "12h": "Last 12 hours",
          "24h": "Last 24 hours",
          "2d": "Last 2 days",
          "7d": "Last 7 days",
          "30d": "Last 30 days",
          "90d": "Last 90 days",
        },
        FollowsRelease: (from, to) => `The window follows the release: ${from} to ${to}.`,
      },
      Refresh: {
        Now: "Refresh now",
        Auto: "Auto refresh",
        Options: {
          off: "Off",
          "10s": "Every 10 seconds",
          "30s": "Every 30 seconds",
          "1m": "Every minute",
          "5m": "Every 5 minutes",
        },
        Updated: (relativeTime) => `Updated ${relativeTime}`,
        Failed: (relativeTime) => `The last refresh failed. Showing what was read ${relativeTime}.`,
        FailedBeforeAnyRead: "The last refresh failed.",
        NextIn: (time) => `Next refresh in ${time}`,
        Paused: "Paused, this window ends in the past",
      },
      Quick: {
        Label: "Quick",
        Filters: {
          errorsOnly: "Errors only",
          slow: "Slow, over 1 s",
          failedDependencies: "Failed dependencies",
          exceptions: "Exceptions",
          hideHealthChecks: "Hide health checks",
        },
      },
      Query: {
        Label: "Query",
        Copy: "Copy query",
        CopyLink: "Copy link",
      },
      Summary: {
        Title: "Summary",
        Subtitle: "Everything the search matches: its figures, and its records over time.",
        Window: (from, to) => `${from} to ${to}`,
        LoadFailed: "The summary could not be read.",
        Tiles: {
          // What each figure is called, by the name EngineeringOps gives it (Engineering.LogSearch.Figures holds the ones the code names).
          Labels: {
            records: "Records",
            errors: "Errors",
            slowRequests: "Slowest 5% of requests",
            exceptions: "Exceptions",
            errorsSinceRelease: "Errors since the release",
            failedRequests: "Failed requests",
            newExceptionTypes: "New exception types",
            openIssues: "Open issues",
            new: "New",
            growing: "Growing",
            slowerThanUsual: "Slower than usual",
            requests: "Requests",
            failed: "Failed",
            failedDependencyCalls: "Failed dependency calls",
            recordsInRequest: "Records in this request",
            took: "Took",
            result: "Result",
            failedAt: "Failed at",
          },
          // Under Records: how many of them are requests. The rest is what those requests wrote and called.
          AreRequests: (requests, shown) => (requests === 1 ? "1 is a request" : `${shown} are requests`),
          // Under Errors: what failed, one wording per record type (Engineering.LogSearch.RecordTypes).
          ErrorKinds: {
            request: (errors, shown) => (errors === 1 ? "1 failed request" : `${shown} failed requests`),
            dependency: (errors, shown) => (errors === 1 ? "1 failed dependency call" : `${shown} failed dependency calls`),
            exception: (errors, shown) => (errors === 1 ? "1 exception" : `${shown} exceptions`),
            trace: (errors, shown) => (errors === 1 ? "1 error trace" : `${shown} error traces`),
          },
          // Between the items of a list written under a figure.
          ListSeparator: ", ",
          // A share too small or too large to round honestly: "<0.1% of requests", ">99% of requests".
          LessThan: (amount) => `<${amount}`,
          MoreThan: (amount) => `>${amount}`,
          OrLonger: "or longer",
          NoDuration: "No requests",
          InTheSpanBefore: (count, span) => `${count} in the ${span} before`,
          Before: (value) => `${value} before`,
          GroupedFrom: (errors) => `grouped from ${errors} errors`,
          NotSeenIn: (baseline) => `not seen in ${baseline}`,
          ShareOfRequests: (percent) => `${percent}% of requests`,
          AllTo: (target) => `all to ${target}`,
          OrMore: (count) => `${count} or more`,
          HttpStatus: (statusCode) => `HTTP ${statusCode}`,
          NoRequest: "No request record",
          NothingFailed: "Nothing failed",
          NoneInTheFirst: (count) => `None in the first ${count}`,
          After: (duration) => `after ${duration}`,
        },
        Chart: {
          Title: "Records over time",
          Success: "Successful",
          Warnings: "Warnings",
          Errors: "Errors",
          Release: "Release",
          ZoomHint: "Drag across the chart to zoom into a time window.",
          ZoomOut: "Zoom out",
          ResetZoom: "Reset zoom",
          Bar: (when, records, warnings, errors) => `${when}: ${records} records, ${warnings} warnings, ${errors} errors`,
          Empty: "No records in this window.",
        },
      },
      Findings: {
        Title: "Findings",
        Subtitle: "What stands out, worked out from the search's own counts. Choose one to see its records.",
        Show: "Show these records",
        Comparing: "Comparing with the baseline.",
        LoadFailed: "The findings could not be read.",
        None: "Nothing new, growing or slower than the baseline.",
        NoneInFeature: "No dependencies and no errors in this feature yet.",
        // The baseline reaches further back than the log source keeps, so nothing was compared.
        WindowNotCompared: (kept) =>
          `Nothing to compare with: logs are kept for ${kept}, and the period this window is compared with reaches further back. Choose a shorter or a more recent window.`,
        ReleaseNotCompared: (kept) => `Nothing to compare with: logs are kept for ${kept}, and the time before this release reaches further back.`,
        Kinds: {
          new: "New",
          growing: "Growing",
          slower: "Slower",
          failing: "Failing",
          healthy: "Healthy",
          usual: "Usual",
        },
        // What each group of findings is headed, by the name EngineeringOps gives it. A heading is written for the baseline the mode compared with.
        Groups: {
          newSinceRelease: () => "New since the release",
          worseSinceRelease: () => "Worse since the release",
          issues: (baseline) => `Issues, compared with ${baseline}`,
          slowerThanUsual: () => "Slower than usual",
          dependencies: () => "What this feature depends on",
          featureErrors: () => "Errors in this feature",
        },
        Titles: {
          ErrorsOn: (operation) => `Errors on ${operation}`,
          TargetFailing: (target) => `${target} failing`,
        },
        Meta: {
          Times: (count) => (count === "1" ? "once" : `${count} times`),
          FirstSeen: (count, when) => `${count === "1" ? "once" : `${count} times`}, first seen ${when}`,
          NoneBefore: (count) => `${count === "1" ? "once" : `${count} times`}, none before`,
          Was: (count, before) => `${count === "1" ? "once" : `${count} times`}, was ${before}`,
          // The baseline lists too many values to hold this one, so only the most it can have been is known.
          WasAtMost: (count, before) => `${count === "1" ? "once" : `${count} times`}, was ${before} at most`,
          SlowerWas: (duration, before) => `slowest 5% is ${duration}, was ${before}`,
          FailedCalls: (errors, records) => `${errors} of ${records} calls failed`,
          HealthyCalls: (records) => (records === "1" ? "1 call, which did not fail" : `${records} calls, none failed`),
          ErrorsOf: (errors, records) => `${errors} of ${records} records are errors`,
        },
      },
      Trail: {
        Title: "Request trail",
        Subtitle: "Every record the request wrote, oldest first, drawn against the time the request took.",
        Summary: (count, requestId) =>
          `${count === 1 ? "1 record" : `${count} records`} in request ${requestId}, oldest first`,
        Offset: (offset) => `+${offset}`,
        Empty: "No records were found for this request in this window.",
        NoRequest: "This record was written outside any request.",
        EnterRequest: "Enter a request ID above to see everything that request did.",
        LoadFailed: "The request's records could not be read.",
        Truncated: (count) => `Showing the first ${count} records of this request.`,
      },
      Facets: {
        Title: "Filter by value",
        Subtitle: "Tick a value to keep or drop it. Choose its name to keep only that value.",
        Fields: {
          app: "App",
          type: "Type",
          outcome: "Outcome",
          origin: "Origin",
          status: "Status code",
          operation: "Operation",
          target: "Dependency target",
          exception: "Exception type",
        },
        // The apps of the AppSpace a search did not read, under the App group: each reason once,
        // with the names of its apps (Engineering.LogSearch.AppLeftOutReasons).
        NotSearched: "Not searched",
        LeftOutReasons: {
          noAccess: (names) => `No access to the logs in this environment: ${names.join(", ")}`,
          noLogSource: (names) => `No log source in this environment: ${names.join(", ")}`,
        },
        Types: {
          request: "Requests",
          dependency: "Dependencies",
          trace: "Traces",
          exception: "Exceptions",
        },
        Outcomes: {
          success: "Success",
          warning: "Warning",
          error: "Error",
        },
        Origins: {
          health: "Health checks",
          app: "Everything else",
        },
        Find: {
          app: "Find an app",
          operation: "Find an operation",
          target: "Find a target",
          exception: "Find an exception type",
        },
        ShowAll: "Show all",
        Only: (value) => `Show only ${value}`,
        NoMatch: "Nothing matches.",
        Empty: "None in this search.",
        LoadFailed: "The values could not be read.",
        More: (count) => `Show ${count} more`,
        Fewer: "Show fewer",
      },
      Records: {
        Title: "Records",
        Subtitle: "Newest first. Open a record for its request trail, its details and its raw form.",
        Showing: (shown, total) => `Showing ${shown} of ${total} records, newest first`,
        Matching: (shown, total) => `Showing ${shown} of ${total} matching records, newest first`,
        // Until the search's own total has been read
        ShowingSome: (shown) => `Showing ${shown} records, newest first`,
        MatchingSome: (shown) => `Showing ${shown} matching records, newest first`,
        New: (count, more) => `${count}${more ? " or more" : ""} new ${count === 1 && !more ? "record" : "records"}, show them`,
        GroupByRequest: "Group by request",
        Download: "Download",
        Columns: {
          Time: "Time",
          Type: "Type",
          Status: "Status",
          Summary: "Summary",
          Duration: "Duration",
          RequestId: "Request ID",
          Link: "Link",
        },
        Types: {
          request: "Request",
          dependency: "Dependency",
          trace: "Trace",
          exception: "Exception",
        },
        DependencyOk: "OK",
        DependencyFailed: "Failed",
        ExceptionStatus: "Error",
        RequestGroup: (requestId, count) => `Request ${requestId}, ${count === 1 ? "1 record" : `${count} records`}`,
        OutsideRequests: (count) => `${count === 1 ? "1 record" : `${count} records`} outside any request`,
        Empty: {
          Title: "No records match this search",
          Description: "Try a wider time range, or remove a filter.",
          Clear: "Clear filters",
        },
        NoLogSource: {
          Title: "Monitoring is not set up for this environment",
          Description:
            "This app has no log source here, so there is nothing to search. Monitoring is set up when an app is created, with Provision Monitoring ticked.",
          SeveralAppsDescription: (place) =>
            `No app of this ${place} has a log source here, so there is nothing to search. Monitoring is set up when an app is created, with Provision Monitoring ticked.`,
        },
        LoadFailed: "The records could not be read.",
        LoadOlder: "Load older records",
        LoadOlderFailed: "Older records could not be read.",
        NoOlder: "No older records in this window.",
        CopyLink: "Copy a link to this record",
        Linked: "Linked record. The link opened this record. It is not among the records the search shows below.",
        Keys: {
          NextKey: "J",
          PreviousKey: "K",
          Move: "move between records",
          OpenKey: "Enter",
          Open: "open",
          Search: "search",
        },
      },
      Record: {
        Tabs: {
          trail: "Request trail",
          details: "Details",
          raw: "Raw",
        },
        OnlyThisRequest: "Show only this request",
        CopyJson: "Copy as JSON",
        CopyLink: "Copy link",
        LoadFailed: "This record could not be read.",
        NotFound: "This record is no longer in the log source.",
        Properties: "Application properties",
        StackTrace: "Stack trace",
        Succeeded: "Succeeded",
        Failed: "Failed",
        Fields: {
          RequestId: "Request ID",
          Time: "Time (UTC)",
          Operation: "Operation",
          Url: "URL",
          Result: "Result",
          Duration: "Duration",
          DependencyKind: "Dependency type",
          Target: "Target",
          Command: "Command",
          Severity: "Severity",
          Message: "Message",
          ExceptionType: "Exception type",
          FailedMethod: "Failed method",
          Assembly: "Assembly",
          ClientIp: "Client IP",
          Location: "Location",
          Browser: "Browser",
          OperatingSystem: "Operating system",
          ApplicationVersion: "App version",
          Machine: "Machine",
        },
      },
    },
    // An app's CI and CD sections: a build, or a deployment to one environment, that has not
    // finished can be cancelled, and a deployment shows how far it has got and opens to its steps.
    Pipelines: {
      CancelBuild: {
        Label: "Cancel Build",
        Cancelling: "Cancelling",
        NoAccess: "You do not have permission to cancel this app's builds.",
        RequestedTitle: "Build cancellation requested",
        Requested: (buildId) => `Build #${buildId} is being cancelled and stops in a few seconds.`,
        FailedTitle: "Build not cancelled",
        Failed: "The build could not be cancelled. Please try again shortly.",
      },
      CancelDeployment: {
        Label: "Cancel Deploy",
        Cancelling: "Cancelling",
        NoAccess: "You do not have permission to cancel deployments to this environment.",
        RequestedTitle: "Deployment cancellation requested",
        Requested: (environmentName) =>
          `The deployment to ${environmentName} is being cancelled and stops in a few seconds.`,
        FailedTitle: "Deployment not cancelled",
        Failed: "The deployment could not be cancelled. Please try again shortly.",
      },
      // A deployment in a stage card of the CD section: the line under it while it runs (its other
      // states keep their status words), the bar of its finished steps, and why it does not open.
      Deployment: {
        DeployingTo: (environmentName) => `Deploying to ${environmentName}`,
        Steps: "Steps",
        NoAccess: "You do not have permission to see the steps of deployments to this environment.",
        NotStarted: "This deployment has not started, so it has no steps yet.",
      },
      // The steps of the deployment opened in a release's stage cards, shown under the cards.
      DeploymentSteps: {
        Title: (environmentName, deploymentId) => `${environmentName} deployment #${deploymentId}`,
        Close: "Close the steps",
      },
    },
    // An app's Codebase tab: the card that shows the address of the app's repository.
    Codebase: {
      RepositoryCard: {
        Title: "Repository",
        Subtitle: "URL",
      },
    },
    // The Test Coverage tab: the unit test results and line coverage of an app's builds, as Asset
    // Health's test coverage scans read them. Shown for one app, or for every app of an AppSpace,
    // organisation or tenant together. A percentage arrives with one decimal place.
    TestCoverage: {
      SectionTitle: "Test Coverage",
      // Short enough to keep Scan now, and the running scan's link beside it, on its line.
      SectionDescription: "Unit test results and line coverage of this app's builds.",
      // place: what a view of several apps covers (Copy.Common.Places).
      SeveralAppsDescription: (place) =>
        `Unit test results and line coverage of every app of this ${place}.`,
      Percentage: (percentage) => `${percentage.toFixed(1)}%`,
      // A percentage figure's value and unit, apart, as a figure card draws them.
      PercentageValue: (percentage) => percentage.toFixed(1),
      PercentageUnit: "%",
      // In place of a figure the scan has none of, such as the line coverage of a build that ran its
      // tests and wrote no coverage file.
      None: "None",
      // How many of a scan's builds had their test results read, of those that finished in its window.
      BuildsWithResults: (withResults, finished) => `${withResults} of ${finished}`,
      // A build as a chart's tooltip names it: when it finished, in local time, and its branch.
      BuildName: (finishedAt, branch) => (branch ? `${finishedAt}, ${branch}` : finishedAt),
      // A line coverage change, in percentage points, against the previous completed scan.
      Change: (points) => (points > 0 ? `+${points.toFixed(1)} points` : points < 0 ? `${points.toFixed(1)} points` : "No change"),
      ScanNow: {
        Label: "Scan now",
        InProgress: "Scan in progress…",
        ViewJob: "Open the running scan in the Job Orchestrator",
        // What the shared job runner hook says as a scan is asked for and when it ends.
        Notifications: {
          Enqueued: { Title: "Scan queued", Message: "The test coverage scan is queued. Its results show here when it finishes." },
          Completed: { Title: "Scan finished", Message: "The test coverage scan has finished. What it found is in the scan history." },
          // {error}: the hook puts the reason JobRunner gave here.
          Failed: { Title: "Scan not queued", Message: "{error}" },
          NoProduct: { Title: "No app to scan", Message: "Open an app to scan its builds." },
          NoTemplate: { Title: "Scan not available", Message: "The test coverage scan could not be set up. Refresh the page and try again." },
        },
      },
      // The figure cards at the top of the tab.
      Figures: {
        // What the cards are called where they cannot be drawn.
        Title: "Figures",
        LineCoverage: { Title: "Line coverage", Description: "Latest build with results" },
        TestsPassed: { Title: "Tests passed", Description: "Unit tests of that build", OfTotal: (total) => `of ${total}` },
        TestsFailed: { Title: "Tests failed", Description: "Unit tests of that build" },
        TestsSkipped: { Title: "Tests skipped", Description: "Unit tests of that build" },
        AppsWithCoverage: { Title: "Apps with coverage", Description: "In their latest scan", OfTotal: (total) => `of ${total}` },
        AverageLineCoverage: { Title: "Average line coverage", Description: "Of the apps with coverage" },
        FallingCoverage: { Title: "Falling coverage", Description: "Apps yet to recover" },
        AppsWithFailingTests: { Title: "Apps with failing tests", Description: "In their latest results" },
        // The row under a line coverage figure saying how it moved against the scan before.
        Change: "Change",
      },
      // In place of the figures and charts, for an app with no scan to show, or whose latest scan
      // read no test results.
      LatestScan: {
        Title: "Latest scan",
        Subtitle: "This app's latest test coverage scan",
        LoadFailed: "This app's test coverage could not be read.",
        NoScan: {
          Title: "No test coverage scan yet",
          Description:
            "Scan now reads the unit test results and line coverage of this app's recent builds. A recurring scan can be set up in the Job Orchestrator.",
          Action: "Open Job Orchestrator",
        },
        // Why the latest scan has no results, keyed by the name of the outcome Asset Health sent (its
        // TestCoverageScanOutcome), each a function of the days the scan looked at
        // (Copy.Common.DayRange; none for a scan that has not run). A scan whose results were read
        // shows them instead, so TestResultsRead has no words here.
        Outcomes: {
          NoFinishedBuilds: (range) =>
            range
              ? `No build of this app finished from ${range}, not counting pull request checks.`
              : "No build of this app finished in the scan's window, not counting pull request checks.",
          TestResultsNotReadable: () => "The test results of this app's builds could not be read.",
          TestResultsNotReadYet: () => "The test results of this app's latest builds have not been read yet.",
          NoTestResultsPublished: () => "This app's builds do not publish test results yet.",
        },
      },
      // The builds of the latest scan's window, read from EngineeringOps when the scan is served.
      LatestScanBuilds: {
        LoadFailed: "This app's latest scan could not be read.",
        Unavailable: "The builds of this app's latest scan could not be read just now.",
      },
      // The problems open at the app's latest completed scan, as Asset Health raised them. One stays
      // open from scan to scan until a scan finds it gone.
      Problems: {
        Title: "Problems",
        Subtitle: "Open at this app's latest scan, oldest first",
        None: "This app has no open problems.",
        LoadFailed: "This app's open problems could not be read.",
        Columns: {
          Problem: "Problem",
          OpenSince: "Open since",
          Severity: "Severity",
        },
        // What each problem is, keyed by the name of its kind as Asset Health sent it (its
        // TestCoverageFindingKind).
        Kinds: {
          LineCoverageFall: "Falling line coverage",
        },
        // Under what a problem is: the line coverage before the fall, and the one read while it is open.
        Fell: (from, to) => `Fell from ${from} to ${to}`,
      },
      LineCoverage: {
        Title: "Line coverage per build",
        // range: the days the scan looked at (Copy.Common.DayRange).
        Subtitle: (range) => (range ? `Line coverage of each build with test results, ${range}` : "Line coverage of each build with test results"),
        Latest: "Latest",
        Average: "Average",
        Series: "Line coverage",
        NoCoverage: "No build of the latest scan has a line coverage figure.",
        // A label of the chart's value axis, from 0 to 100.
        AxisLabel: (percentage) => `${percentage}%`,
      },
      Tests: {
        Title: "Tests per build",
        Subtitle: (range) =>
          range ? `Unit tests passed and failed in each build with test results, ${range}` : "Unit tests passed and failed in each build with test results",
        PassRate: "Pass rate",
        BuildsWithResults: "Builds with results",
        NoResults: "No build of the latest scan has test results.",
        Passed: "Passed",
        Failed: "Failed",
        // Under a build's column: how many of its tests passed, or failed.
        TestCount: (count) => (count === 1 ? "1 test" : `${count} tests`),
      },
      Builds: {
        Title: "Builds",
        // Asset Health leaves out the builds a pull request started (decided 2026-10-22).
        Subtitle: (range) =>
          range
            ? `Builds that finished from ${range}, newest first, without pull request checks`
            : "Builds of the latest scan's window, newest first, without pull request checks",
        None: "No build finished in the latest scan's window.",
        Count: (count) => (count === 1 ? "1 build" : `${count} builds`),
        Columns: {
          Finished: "Finished",
          Result: "Result",
          Tests: "Tests",
          LineCoverage: "Line coverage",
          TestResults: "Test results",
        },
        // The build's result, by the word EngineeringOps sends (Engineering.TestCoverage.BuildResults).
        Results: {
          Succeeded: "Succeeded",
          Failed: "Failed",
        },
        // What was read from the build, by Engineering.TestCoverage.ReportStates.
        ReportStates: {
          Read: "Read",
          NoArtifact: "Not published",
          ArtifactNotReadable: "Unreadable",
        },
        NotReadYet: "Not read yet",
      },
      // A build's or an app's unit tests, part by part.
      TestCounts: {
        Passed: (count) => `${count} passed`,
        Failed: (count) => `${count} failed`,
        Skipped: (count) => `${count} skipped`,
        Separator: ", ",
      },
      // Of several apps: each app's latest completed scan.
      Apps: {
        Title: "Apps",
        Subtitle: (place) => `The latest test coverage scan of each app of this ${place}`,
        LoadFailed: (place) => `The test coverage of this ${place}'s apps could not be read.`,
        NoScans: {
          Title: (place) => `No app of this ${place} has a completed test coverage scan yet`,
          Description:
            "Scan an app now from its own Test Coverage tab, or set up a recurring scan in the Job Orchestrator.",
          Action: "Open Job Orchestrator",
        },
        Count: (count) => (count === 1 ? "1 app" : `${count} apps`),
        Columns: {
          App: "App",
          LineCoverage: "Line coverage",
          Problems: "Problems",
          Tests: "Tests",
          LatestReportedBuild: "Latest results",
          Scanned: "Scanned",
        },
      },
      // The line coverage by day of the window, from the builds and the scans: one app's, or each
      // app's of several. A line holds its last figure on the days with no new one.
      Trend: {
        Title: "Line coverage over time",
        // range: the days the window covers (Copy.Common.DayRange).
        Subtitle: (range) =>
          range ? `This app's line coverage by day, from its builds and scans, ${range}` : "This app's line coverage by day, from its builds and scans",
        SeveralAppsSubtitle: (range) =>
          range ? `Each app's line coverage by day, from its builds and scans, ${range}` : "Each app's line coverage by day, from its builds and scans",
        NoCoverage: "No build or scan of this app has a line coverage figure in this window.",
        SeveralAppsNoCoverage: "No app has a line coverage figure in this window.",
        // When EngineeringOps could not answer for the builds, the lines are drawn from the scans alone.
        BuildsUnavailable: "This app's builds could not be read just now, so its line shows its scans only.",
        SeveralAppsBuildsUnavailable: "Some apps' builds could not be read just now, so their lines show their scans only.",
        // Under a day's figure in the tooltip: the build or scan it came from, by its local time.
        FromBuild: (finishedAt, branch) => (branch ? `From the build of ${finishedAt}, ${branch}` : `From the build of ${finishedAt}`),
        FromScan: (completedAt) => `From the scan of ${completedAt}`,
        // A label of the chart's value axis, from 0 to 100.
        AxisLabel: (percentage) => `${percentage}%`,
      },
      ScanHistory: {
        Title: "Scan history",
        Subtitle: "This app's test coverage scans, newest first",
        // place: what a view of several apps covers (Copy.Common.Places).
        SeveralAppsSubtitle: (place) => `The test coverage scans of every app of this ${place}, newest first`,
        LoadFailed: "The scan history could not be read.",
        None: "Each scan shows here from the moment it is asked for.",
        Count: (count) => (count === 1 ? "1 scan" : `${count} scans`),
        Columns: {
          App: "App",
          Requested: "Requested",
          BuildsWithResults: "Builds with results",
          LineCoverage: "Line coverage",
          Problems: "Problems",
          PassRate: "Pass rate",
          Status: "Status",
        },
        // Where a scan stands, by Engineering.TestCoverage.ScanStatuses.
        Statuses: {
          Pending: "Waiting",
          Running: "Running",
          Completed: "Completed",
          Failed: "Failed",
        },
      },
    },
    // The fields of a recurring scan in the Job Orchestrator whose one parameter is what it scans:
    // one panel for every scan (ScanScopePicker, decided 2026-10-22). The panel's own
    // words first, then each scan's lines.
    ScanScopePicker: {
      // What the form's check calls the chosen organisation, AppSpace or product.
      ScopeEntity: "Scope entity",
      // By scope level (Onboarding.ScopeLevels): the picker's label, the level's name in a sentence,
      // the level with its article, and what the picker shows before a choice.
      Scopes: {
        Organisation: {
          Label: "Organisation",
          Name: "organisation",
          WithArticle: "an organisation",
          Placeholder: "Select an organisation",
        },
        AppSpace: {
          Label: "AppSpace",
          Name: "AppSpace",
          WithArticle: "an AppSpace",
          Placeholder: "Select an AppSpace",
        },
        Product: {
          Label: "Product",
          Name: "product",
          WithArticle: "a product",
          Placeholder: "Select a product",
        },
      },
      NoProducts: "This AppSpace has no products yet. Provision an API or a web app first, or choose a different scope level.",
      NoneToChoose: (scopeName) => `No ${scopeName} is available to select. Choose a different scope level.`,
      // Each scan's lines, by the scan: at the Tenant level, once an entity is chosen (scopeName:
      // the level's name in a sentence), and while none is (scope: the level with its article).
      Scans: {
        PackageDependencyVulnerability: {
          TenantScope: "The vulnerability scan will target all repositories across the entire tenant.",
          Target: (scopeName, name) => `The vulnerability scan will target ${scopeName} "${name}". Ensure it has at least one repository with dependency manifests.`,
          Choose: (scope) => `Select ${scope} above so the scan knows which repositories to analyse.`,
        },
        PackageEndOfLife: {
          TenantScope: "The EOL check will target all repositories across the entire tenant.",
          Target: (scopeName, name) => `The EOL check will target ${scopeName} "${name}". Ensure it has at least one repository with dependency manifests.`,
          Choose: (scope) => `Select ${scope} above so the check knows which repositories to analyse.`,
        },
        DevSecOpsMaturity: {
          TenantScope: "The DevSecOps maturity scan will target all repositories across the entire tenant.",
          Target: (scopeName, name) => `The DevSecOps maturity scan will target ${scopeName} "${name}". Ensure it has at least one repository with CI/CD pipelines configured.`,
          Choose: (scope) => `Select ${scope} above so the scan knows which repositories to analyse.`,
        },
        Standards: {
          TenantScope: "The standards scan will target all repositories across the entire tenant.",
          Target: (scopeName, name) => `The standards scan will target ${scopeName} "${name}". Ensure it has at least one repository with standards configuration.`,
          Choose: (scope) => `Select ${scope} above so the scan knows which repositories to analyse.`,
        },
        TestCoverage: {
          TenantScope: "The test coverage scan will read the builds of every app across the entire tenant.",
          Target: (scopeName, name) => `The test coverage scan will read the builds of ${scopeName} "${name}". Its builds need to publish test results.`,
          Choose: (scope) => `Select ${scope} above so the scan knows which builds to read.`,
        },
      },
    },
    // The release check of a release job, on the job's page while it waits on the app's open critical
    // findings (JobOrchestrator/ReleaseCheck), and the dialog an owner accepts one of them in.
    ReleaseCheck: {
      Title: "Release check",
      Subtitle:
        "The release waits while the app has an open critical finding. A finding stops holding it once a new scan no longer finds it, or once an owner of the app accepts it.",
      Blocked: "Blocked",
      // The scan that found a finding, by Asset Health's name for it.
      Scans: {
        DependencyVulnerabilities: "Vulnerability scan",
        PackageEol: "End-of-life check",
        Standards: "Standards scan",
        DevSecOpsMaturity: "DevSecOps maturity scan",
      },
      // Under a finding's title: the scan that found it and when it was opened, in local time.
      FoundBy: (scanName, openedAt) => `${scanName}, opened ${openedAt}`,
      NoneOpen: "No critical finding is open now, so the release check will let this release go.",
      ReadFailed: "The critical findings that hold this release could not be read.",
      Accept: "Accept",
      // What a screen reader says for a finding's Accept button.
      AcceptFinding: (title) => `Accept ${title}`,
      // Why Accept is turned off, and why Asset Health refused an acceptance: only these people may accept.
      OwnersOnly: "Only an owner of this app, its AppSpace, its organisation or the tenant can accept a finding.",
      AcceptFindingDialog: {
        Title: "Accept this finding",
        Description:
          "Once accepted, the finding no longer holds this release, and it stays accepted when later scans find it again. Who accepted it, when and why are kept with it.",
        ReasonLabel: "Reason",
        ReasonPlaceholder: "Why can this finding stay? For example: the vulnerable code is never called, and the fix ships next week.",
        // How long the reason is once trimmed, as Asset Health counts it, and the lengths it accepts.
        ReasonLength: (length, minimum, maximum) => `${length} of ${maximum} characters, at least ${minimum}.`,
        ReasonInvalid: (minimum, maximum) => `Write why the finding can stay, in ${minimum} to ${maximum} characters.`,
        Accept: "Accept finding",
        AcceptedTitle: "Finding accepted",
        Accepted: "It no longer holds this release. Your reason is kept with it.",
        NotAcceptedTitle: "Finding not accepted",
      },
    },
    // The words for a status the job runner gives a job or a step, where the Job Orchestrator's
    // status maps (JobOrchestrator/types/ui.ts) take them from copy.
    JobStatuses: {
      // A job, or one of its steps, that waits for a person before anything more runs.
      WaitingForApproval: "Waiting for approval",
    },
    // The agent budget of a job with an AI agent step, on the job's page while the job waits at one
    // of its limits (JobOrchestrator/AgentBudget), and the dialog an owner of the job's AppSpace, its
    // organisation or the tenant raises the limits in.
    AgentBudget: {
      Title: "Agent budget",
      Subtitle:
        "This job's AI agent reached one of its limits, so the job waits. Nothing more runs until an owner of this AppSpace, its organisation or the tenant raises the limits.",
      // The same for a job that belongs to no AppSpace, whose limits cannot be raised.
      NoAppSpaceSubtitle:
        "This job's AI agent reached one of its limits, so the job waits. Its limits cannot be raised, because the job belongs to no AppSpace; cancelling the job ends the wait.",
      ModelCalls: "AI model calls",
      ModelCallLimit: "AI model call limit",
      TokensSent: "Tokens sent to the model",
      TokensSentBack: "Tokens the model sent back",
      TotalTokens: "Total tokens",
      TotalTokenLimit: "Total token limit",
      RaiseLimits: "Raise limits",
      // Why the job runner refused a change of the limits with an empty 403: only these people may make one.
      OwnersOnly: "Only an owner of this AppSpace, its organisation or the tenant can raise the limits.",
      ChangeAgentBudgetDialog: {
        Title: "Raise the limits",
        Description:
          "Type the new limits. The job then runs its AI agent step again. Who raised the limits, and from what to what, is kept with the job.",
        // Under each limit's box: what the job has used, already written as a number.
        ModelCallsUsed: (used) => `Used so far: ${used}.`,
        TokensUsed: (used) => `Used so far: ${used}, sent and sent back together.`,
        // A limit that is not a whole number of at least 1. Whether it is above what is used, the job
        // runner says.
        LimitInvalid: "Type a whole number of 1 or more.",
        RaisedTitle: "Limits raised",
        Raised: "The job runs its AI agent step again.",
        NotRaisedTitle: "Limits not raised",
      },
    },
    // The Health & Compliance Overview: a card per domain, each with its score from Asset Health's
    // overview answer.
    HealthOverview: {
      DomainCards: {
        // In place of a domain's score when its entry has no figure to score: Test Coverage when no
        // app in scope has a line coverage figure.
        NoFigure: "No figure",
        TestCoverage: {
          // The card's tooltip, in the form of the other domains' cards: what it answers, then how.
          Formula:
            "How much of your code do the unit tests run? The average line coverage of each app's latest test coverage scan, over the apps whose builds report coverage.\n\nFormula: sum of each app's latest line coverage ÷ apps with a line coverage figure",
          // How many apps have a fall of line coverage open at their latest scan.
          FallingCoverage: (count) =>
            count === 0 ? "No app with falling coverage" : count === 1 ? "1 app with falling coverage" : `${count} apps with falling coverage`,
        },
      },
    },
    // The Traffic tab: what ProxyApi saw of the calls to an app, or to every app of a wider level.
    Traffic: {
      ResponseTimeTrends: {
        Title: "Response Time Trends",
      },
    },
  },
  FeatureManagement: {
    SectionTitle: "Feature flags",
    SectionDescription:
      "The app's toggles: what each one serves in an environment, who gets something else, how often it is read, and the keys the app reads them with.",
    EnvironmentLabel: "Environment",
    LoadFailed: "The toggles could not be read. Please try again shortly.",
    ChangeFailedTitle: "Not saved",
    Toggles: {
      Title: "Toggles",
      Subtitle: (count) => (count === 1 ? "1 toggle" : `${count} toggles`),
      New: "New toggle",
      None: "This app has no toggles yet.",
      Columns: { Key: "Key", Name: "Name", ValueType: "Type", Made: "Made" },
      Archived: "Archived",
      ActionsFor: (key) => `Actions for ${key}`,
      Open: "Open",
      Edit: "Edit",
      Archive: "Archive",
      Restore: "Restore",
      Delete: "Delete",
      ArchivedTitle: "Toggle archived",
      ArchivedMessage: (key) => `${key} is served nowhere now. Its settings are kept.`,
      RestoredTitle: "Toggle restored",
      RestoredMessage: (key) => `${key} is served again as its settings say.`,
      DeleteTitle: "Delete this toggle?",
      DeleteDescription: (key) =>
        `${key} and its settings in every environment are removed for good. Code that reads it is served its own default from then on.`,
      DeleteConfirm: "Delete toggle",
      DeletedTitle: "Toggle deleted",
      DeletedMessage: (key) => `${key} was removed.`,
    },
    ToggleForm: {
      NewTitle: "New toggle",
      EditTitle: "Edit toggle",
      NewSubtitle: "A toggle's key and type cannot be changed once it is made.",
      EditSubtitle: "A toggle's key and type cannot be changed.",
      Key: "Key",
      KeyHint: "Lower-case words and numbers joined by hyphens, for example checkout-experience.",
      KeyInvalid: "Use lower-case words and numbers joined by hyphens.",
      Name: "Name",
      NameRequired: "Give the toggle a name.",
      Description: "Description",
      ValueType: "Type",
      Save: "Save toggle",
      SavedTitle: "Toggle saved",
      SavedMessage: (key) => `${key} was saved.`,
    },
    ValueTypes: {
      Boolean: "True or false",
      String: "Text",
      Integer: "Whole number",
      Double: "Number",
      Object: "Structure (JSON)",
    },
    Setting: {
      Title: (key, environmentName) => `${key} in ${environmentName}`,
      Subtitle: "Whether the toggle is on here, and the value it serves when no criterion matches.",
      On: "On",
      OnHint: "Switched off, the toggle serves each caller's own default: the kill switch.",
      DefaultValue: "Default value",
      True: "true",
      False: "false",
      NoSettingYet: (environmentName) =>
        `This toggle has no setting in ${environmentName} yet, so it is not served there. Save one to serve it.`,
      Save: "Save setting",
      SavedTitle: "Setting saved",
      SavedMessage: (key, environmentName) => `${key} was saved in ${environmentName}.`,
      ReadOnly: (environmentName) =>
        `You do not have permission to change toggles in ${environmentName}. Contact your system administrator to adjust your permissions.`,
    },
    Criteria: {
      Title: "Criteria",
      Subtitle: "Who is served something else. The first criterion that matches a caller decides.",
      New: "Add criterion",
      None: "No criteria: every caller is served the default value.",
      NeedsSetting: "Save the toggle's setting in this environment first.",
      Columns: { Order: "Order", Rule: "Who", Serves: "Serves" },
      ActionsFor: (order) => `Actions for criterion ${order}`,
      MoveUp: "Move up",
      MoveDown: "Move down",
      Edit: "Edit",
      Delete: "Delete",
      DeleteTitle: "Delete this criterion?",
      DeleteDescription: "Callers it matched are served by the next criterion that matches them, or the default value.",
      DeleteConfirm: "Delete criterion",
      DeletedTitle: "Criterion deleted",
      DeletedMessage: "The criterion was removed.",
      ReorderedTitle: "Order saved",
      ReorderedMessage: "The criteria are tried in the new order.",
      Operators: {
        In: "is one of",
        NotIn: "is none of",
        Equals: "is",
        StartsWith: "starts with",
        Contains: "contains",
        GreaterThan: "is greater than",
        LessThan: "is less than",
      },
      AttributeRule: (attribute, operatorWords, values) => `${attribute} ${operatorWords} ${values}`,
      KeptList: (size) => `a list of ${size.toLocaleString()} values`,
      PercentageRule: (percentage, attribute) => `${percentage}% of callers, shared out by ${attribute}`,
    },
    CriterionForm: {
      NewTitle: "Add criterion",
      EditTitle: "Edit criterion",
      Subtitle: "A criterion serves its value to the callers it matches.",
      Kind: "Kind",
      Kinds: { Attribute: "By an attribute of the caller", Percentage: "A share of callers" },
      Attribute: "Attribute",
      AttributeHint: "The name of the attribute the app sends for a caller, for example plan or userId.",
      Operator: "Comparison",
      Values: "Values",
      ValuesHint: "One value on each line, or separated by commas.",
      UploadList: "Or read the values from a file",
      UploadHint: "A .csv or .txt file with one value on each line. A large list is kept outside the ruleset.",
      ValuesRead: (count, fileName) => `${count.toLocaleString()} values read from ${fileName}.`,
      KeptList: (size) =>
        `A list of ${size.toLocaleString()} values is kept for this criterion. Read a file to replace it, or leave it as it is.`,
      Percentage: "Share of callers (%)",
      DistributionByAttribute: "Shared out by",
      DistributionHint: "The attribute that decides which callers are in the share, so a caller always gets the same answer.",
      ServedValue: "Serves",
      Save: "Save criterion",
      SavedTitle: "Criterion saved",
      SavedMessage: "The criterion was saved.",
      AttributeRequired: "Name the attribute.",
      ValuesRequired: "Give at least one value.",
      PercentageRequired: "Give a share above 0 and up to 100.",
      ServedValueRequired: "Say what the criterion serves.",
    },
    Usage: {
      Title: "Usage",
      Subtitle: (staleCount, staleAfterDays) =>
        staleCount === 1
          ? `1 toggle has not been read for ${staleAfterDays} days.`
          : `${staleCount} toggles have not been read for ${staleAfterDays} days.`,
      StaleOnly: "Only toggles that are no longer read",
      None: "No toggle to show.",
      Columns: { Key: "Key", Hits: "Read", ByCriterion: "By a criterion", ByDefault: "By default", LastHit: "Last read", Unused: "Unused for" },
      Never: "Never",
      Days: (days) => (days === 1 ? "1 day" : `${days} days`),
      Stale: "No longer read",
    },
    Keys: {
      Title: (environmentName) => `Keys for ${environmentName}`,
      Subtitle: "The app presents a key to read its toggles. A key is shown in full once, when it is made or replaced.",
      None: "This app has no keys for this environment yet.",
      Mint: "Make the keys",
      Rotate: "Replace",
      Columns: { Kind: "Kind", StartsWith: "Starts with", Made: "Made", Replaced: "Last replaced" },
      Kinds: { Server: "Server key", Client: "Client key" },
      Never: "Never",
      ActionsFor: (kind) => `Actions for the ${kind} key`,
      RotateTitle: "Replace this key?",
      RotateDescription: (kindName, environmentName) =>
        `The ${kindName} for ${environmentName} stops working at once. Every instance of the app that presents it serves what it holds until it is given the new key.`,
      RotateConfirm: "Replace key",
      NewKeysTitle: "Copy the key now",
      NewKeysDescription: "This is the only time the key is shown in full. Keep it where the app reads its secrets.",
      Done: "I have copied it",
      MintedTitle: "Keys made",
      MintedMessage: (environmentName) => `The app has its keys for ${environmentName}.`,
      RotatedTitle: "Key replaced",
      RotatedMessage: (kindName) => `The ${kindName} was replaced. The old one no longer works.`,
    },
  },
  Proxy: {},
  Website: {},
};
